import { memo, type ReactNode } from "react";
import {
  Alert,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

type EcommerceListControlsProps = {
  searchLabel: string;
  searchPlaceholder: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filterHint?: ReactNode;
  visibleCount: number;
  selectedCount: number;
  selectedFilteredCount: number;
  noun: string;
  whatsAppDisabled: boolean;
  onToggleAll: (checked: boolean) => void;
  onSendWhatsApp: () => void;
};

export const EcommerceListControls = memo(function EcommerceListControls({
  searchLabel,
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  filterHint,
  visibleCount,
  selectedCount,
  selectedFilteredCount,
  noun,
  whatsAppDisabled,
  onToggleAll,
  onSendWhatsApp,
}: EcommerceListControlsProps) {
  return (
    <>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 1, border: 1, borderColor: "divider" }}
      >
        <Stack spacing={2}>
          <TextField
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            fullWidth
            sx={{ maxWidth: { sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Limpar busca"
                    onClick={() => onSearchChange("")}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            inputProps={{ "aria-label": searchPlaceholder }}
          />
          {filterHint}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 1,
          borderColor: "divider",
          border: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Checkbox
              color="primary"
              checked={visibleCount > 0 && selectedFilteredCount === visibleCount}
              indeterminate={
                selectedFilteredCount > 0 && selectedFilteredCount < visibleCount
              }
              disabled={visibleCount === 0}
              onChange={(event) => onToggleAll(event.target.checked)}
              inputProps={{ "aria-label": "Selecionar todos" }}
            />
            <Typography variant="body2" color="text.secondary">
              Selecionar todos ({visibleCount} {noun})
              {selectedCount > 0 ? ` · ${selectedCount} selecionado(s)` : ""}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            disabled={whatsAppDisabled}
            onClick={onSendWhatsApp}
            sx={{
              alignSelf: { xs: "stretch", sm: "auto" },
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Enviar WhatsApp ({selectedCount})
          </Button>
        </Stack>
      </Paper>
    </>
  );
});
